import React, { useState, useEffect, useMemo } from 'react';
import { HiArrowUp, HiArrowDown, HiX } from 'react-icons/hi';
import apiClient from './api/client';
import ModalOverlay from './components/ModalOverlay';

const RULE_SETTINGS_KEY = 'excludedKeywords_ruleSettings';

const loadRuleSettings = () => {
  try {
    const raw = localStorage.getItem(RULE_SETTINGS_KEY);
    if (raw) {
      const data = JSON.parse(raw);
      return {
        ruleShowsOp: data.ruleShowsOp ?? '>=',
        ruleShowsVal: data.ruleShowsVal ?? '',
        ruleCtrOp: data.ruleCtrOp ?? '<=',
        ruleCtrVal: data.ruleCtrVal ?? '',
        ruleOrdersOp: data.ruleOrdersOp ?? '<=',
        ruleOrdersVal: data.ruleOrdersVal ?? '',
        ruleCpoOp: data.ruleCpoOp ?? '>=',
        ruleCpoVal: data.ruleCpoVal ?? '',
        ruleExcludeInput: data.ruleExcludeInput ?? '',
      };
    }
  } catch (e) {
    console.warn('loadRuleSettings:', e);
  }
  return null;
};

const saveRuleSettings = (state) => {
  try {
    localStorage.setItem(RULE_SETTINGS_KEY, JSON.stringify({
      ruleShowsOp: state.ruleShowsOp,
      ruleShowsVal: state.ruleShowsVal,
      ruleCtrOp: state.ruleCtrOp,
      ruleCtrVal: state.ruleCtrVal,
      ruleOrdersOp: state.ruleOrdersOp,
      ruleOrdersVal: state.ruleOrdersVal,
      ruleCpoOp: state.ruleCpoOp,
      ruleCpoVal: state.ruleCpoVal,
      ruleExcludeInput: state.ruleExcludeInput,
    }));
  } catch (e) {
    console.warn('saveRuleSettings:', e);
  }
};

const clearRuleSettings = () => {
  try {
    localStorage.removeItem(RULE_SETTINGS_KEY);
  } catch (e) {
    console.warn('clearRuleSettings:', e);
  }
};

const DEFAULT_RULE_SETTINGS = {
  ruleShowsOp: '>=',
  ruleShowsVal: '100',
  ruleCtrOp: '<=',
  ruleCtrVal: '1',
  ruleOrdersOp: '<=',
  ruleOrdersVal: '',
  ruleCpoOp: '>=',
  ruleCpoVal: '',
  ruleExcludeInput: '',
};

export default function ExcludedKeywords({ clientId }) {
  const [view, setView] = useState('campaigns'); // 'campaigns' или 'keywords'
  const [keywordViewTab, setKeywordViewTab] = useState('active'); // 'active' | 'minus'
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  
  // Данные кампаний
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState(new Set());
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  
  // Данные запросов
  const [activeKeywords, setActiveKeywords] = useState([]);
  const [minusKeywords, setMinusKeywords] = useState([]);
  const [loadingKeywords, setLoadingKeywords] = useState(false);
  const [removingPhrase, setRemovingPhrase] = useState(null);
  
  // Модальное окно правил
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [rulesForCampaigns, setRulesForCampaigns] = useState([]); // массив advert_id
  const [ruleShowsOp, setRuleShowsOp] = useState('>=');
  const [ruleShowsVal, setRuleShowsVal] = useState('');
  const [ruleCtrOp, setRuleCtrOp] = useState('<=');
  const [ruleCtrVal, setRuleCtrVal] = useState('');
  const [ruleOrdersOp, setRuleOrdersOp] = useState('<=');
  const [ruleOrdersVal, setRuleOrdersVal] = useState('');
  const [ruleCpoOp, setRuleCpoOp] = useState('>=');
  const [ruleCpoVal, setRuleCpoVal] = useState('');
  const [ruleExcludeInput, setRuleExcludeInput] = useState('');
  const [applyingRules, setApplyingRules] = useState(false);
  
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Период для данных (null = вся история из сводной таблицы)
  const defaultTo = () => new Date().toISOString().split('T')[0];
  const defaultFrom = () => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    return d.toISOString().split('T')[0];
  };
  const [periodFrom, setPeriodFrom] = useState(defaultFrom);
  const [periodTo, setPeriodTo] = useState(defaultTo);
  const [usePeriod, setUsePeriod] = useState(true); // true = использовать период, false = вся история

  // Сортировка: сводная по кампаниям
  const [campaignSortCol, setCampaignSortCol] = useState('spend');
  const [campaignSortDir, setCampaignSortDir] = useState('desc');
  const sortedCampaigns = useMemo(() => {
    const arr = [...campaigns];
    const key = campaignSortCol;
    const dir = campaignSortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let va = key === 'name'
        ? (a.name || '')
        : key === 'minus_count'
          ? (a.minus_count ?? 0)
          : key === 'cpo'
            ? (a.metrics?.orders > 0 ? (a.metrics?.spend || 0) / a.metrics.orders : null)
            : (a.metrics?.[key] ?? 0);
      let vb = key === 'name'
        ? (b.name || '')
        : key === 'minus_count'
          ? (b.minus_count ?? 0)
          : key === 'cpo'
            ? (b.metrics?.orders > 0 ? (b.metrics?.spend || 0) / b.metrics.orders : null)
            : (b.metrics?.[key] ?? 0);
      if (key === 'name') return dir * String(va).localeCompare(String(vb));
      return dir * ((Number(va) || 0) - (Number(vb) || 0));
    });
    return arr;
  }, [campaigns, campaignSortCol, campaignSortDir]);

  // Сортировка: таблица запросов
  const [keywordSortCol, setKeywordSortCol] = useState('shows');
  const [keywordSortDir, setKeywordSortDir] = useState('desc');
  const [minusSortCol, setMinusSortCol] = useState('shows');
  const [minusSortDir, setMinusSortDir] = useState('desc');

  const sortKeywordRows = (rows, sortCol, sortDir) => {
    const arr = [...rows];
    const key = sortCol;
    const dir = sortDir === 'asc' ? 1 : -1;
    const kMap = { norm_query: 'norm_query', shows: 'shows', clicks: 'clicks', ctr: 'ctr', cpc: 'cpc', cpo: 'cpo', sum: 'sum', orders: 'orders', bid: 'bid', avg_pos: 'avg_pos', last_updated: 'last_updated' };
    const field = kMap[key] || 'shows';
    arr.sort((a, b) => {
      let va = field === 'norm_query' ? (a.norm_query || a.keyword || '') : (field === 'cpo' ? (a.orders > 0 && a.sum != null ? a.sum / a.orders : null) : (a[field] ?? 0));
      let vb = field === 'norm_query' ? (b.norm_query || b.keyword || '') : (field === 'cpo' ? (b.orders > 0 && b.sum != null ? b.sum / b.orders : null) : (b[field] ?? 0));
      if (field === 'norm_query') return dir * String(va).localeCompare(String(vb));
      if (field === 'last_updated') {
        const da = va ? new Date(va).getTime() : 0;
        const db = vb ? new Date(vb).getTime() : 0;
        return dir * (da - db);
      }
      return dir * ((Number(va) || 0) - (Number(vb) || 0));
    });
    return arr;
  };

  const sortedActiveKeywords = useMemo(
    () => sortKeywordRows(activeKeywords, keywordSortCol, keywordSortDir),
    [activeKeywords, keywordSortCol, keywordSortDir]
  );
  const sortedMinusKeywords = useMemo(
    () => sortKeywordRows(minusKeywords, minusSortCol, minusSortDir),
    [minusKeywords, minusSortCol, minusSortDir]
  );

  const toggleCampaignSort = (col) => {
    if (campaignSortCol === col) {
      setCampaignSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setCampaignSortCol(col);
      setCampaignSortDir(col === 'name' ? 'asc' : 'desc');
    }
  };
  const toggleKeywordSort = (col) => {
    if (keywordSortCol === col) {
      setKeywordSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setKeywordSortCol(col);
      setKeywordSortDir(col === 'norm_query' ? 'asc' : 'desc');
    }
  };
  const toggleMinusSort = (col) => {
    if (minusSortCol === col) {
      setMinusSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setMinusSortCol(col);
      setMinusSortDir(col === 'norm_query' ? 'asc' : 'desc');
    }
  };

  // Загрузка кампаний при смене клиента или периода
  useEffect(() => {
    if (!clientId || isNaN(Number(clientId))) return;
    
    loadCampaigns();
  }, [clientId, usePeriod, periodFrom, periodTo]);

  // При смене периода на экране запросов — перезагрузить запросы
  useEffect(() => {
    if (!clientId || view !== 'keywords' || !selectedCampaignId) return;
    const campaign = campaigns.find(c => c.advert_id === selectedCampaignId);
    loadKeywords(selectedCampaignId, campaign?.nm_id);
  }, [usePeriod, periodFrom, periodTo]);

  const campaignHasActiveRule = (advertId) => {
    const campaign = campaigns.find((c) => Number(c.advert_id) === Number(advertId));
    return Boolean(campaign?.has_autominus_rule);
  };

  const rulesButtonClass = (hasRule) => (
    hasRule
      ? 'px-3 py-1 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700 transition-colors'
      : 'px-3 py-1 bg-gray-300 text-gray-800 text-xs rounded hover:bg-gray-400 transition-colors border border-gray-400'
  );

  const loadCampaigns = async () => {
    setLoadingCampaigns(true);
    setError('');
    
    try {
      const params = { client_id: Number(clientId) };
      if (usePeriod && periodFrom && periodTo) {
        params.from = periodFrom;
        params.to = periodTo;
      }
      const res = await apiClient.get('/summary/campaigns-with-metrics', {
        params,
      });
      
      if (res.data && res.data.campaigns && res.data.campaigns.length > 0) {
        const normalizedCampaigns = res.data.campaigns.map(c => ({
          advert_id: c.advert_id,
          name: c.name || `Кампания ${c.advert_id}`,
          campaign_id: c.campaign_id || null,
          nm_id: c.nm_id || null,
          has_autominus_rule: Boolean(c.has_autominus_rule),
          minus_count: Number(c.minus_count) || 0,
          metrics: {
            impressions: Number(c.metrics?.impressions) || 0,
            clicks: Number(c.metrics?.clicks) || 0,
            spend: Number(c.metrics?.spend) || 0,
            orders: Number(c.metrics?.orders) || 0,
            revenue: Number(c.metrics?.revenue) || 0,
            ctr: Number(c.metrics?.ctr) || 0,
            cpc: Number(c.metrics?.cpc) || 0,
            cpo: c.metrics?.cpo !== null && c.metrics?.cpo !== undefined ? Number(c.metrics.cpo) : null,
            avg_pos: c.metrics?.avg_pos != null && c.metrics?.avg_pos !== '' ? Number(c.metrics.avg_pos) : null,
          },
        }));
        setCampaigns(normalizedCampaigns);
      } else {
        // Fallback: список кампаний без метрик (если в normquery_summary ещё нет данных)
        const fallbackRes = await apiClient.get('/summary/campaigns', {
          params: { client_id: Number(clientId) },
        });
        setCampaigns(fallbackRes.data.map(c => ({
          advert_id: c.advert_id,
          name: c.name,
          nm_id: c.nm_id || null,
          has_autominus_rule: Boolean(c.has_autominus_rule),
          minus_count: 0,
          metrics: {
            impressions: 0,
            clicks: 0,
            spend: 0,
            ctr: 0,
            cpc: 0,
            avg_pos: null,
          },
        })));
      }
    } catch (err) {
      console.error('Ошибка загрузки кампаний:', err);
      setError('Ошибка при загрузке кампаний: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const normalizeKeywordRow = (k) => ({
    keyword: k.keyword || k.norm_query || '',
    norm_query: k.norm_query || k.keyword || '',
    shows: Number(k.shows) || 0,
    clicks: Number(k.clicks) || 0,
    ctr: Number(k.ctr) || 0,
    sum: k.sum !== null && k.sum !== undefined ? Number(k.sum) : 0,
    cpc: k.cpc !== null && k.cpc !== undefined ? Number(k.cpc) : 0,
    orders: Number(k.orders) || 0,
    nm_id: k.nm_id || null,
    bid: k.bid !== null && k.bid !== undefined ? Number(k.bid) : null,
    avg_pos: (k.avg_pos ?? k.position) != null && (k.avg_pos ?? k.position) !== '' ? Number(k.avg_pos ?? k.position) : null,
    last_updated: k.last_updated || null,
    cpo: k.cpo != null ? Number(k.cpo) : (k.orders > 0 && (k.sum || 0) > 0 ? Number(k.sum) / Number(k.orders) : null),
  });

  // Загрузка запросов для кампании
  const loadKeywords = async (advertId, nmId) => {
    setLoadingKeywords(true);
    setError('');
    
    try {
      const params = {
        client_id: Number(clientId),
        advert_id: Number(advertId),
      };
      if (usePeriod && periodFrom && periodTo) {
        params.from = periodFrom;
        params.to = periodTo;
      }
      if (nmId) {
        params.nm_id = Number(nmId);
      }
      
      const res = await apiClient.get('/summary/campaign-clusters', { params });
      
      const normalizedActive = (res.data?.active || []).map(normalizeKeywordRow);
      const normalizedMinus = (res.data?.minus || []).map(normalizeKeywordRow);
      
      setActiveKeywords(normalizedActive);
      setMinusKeywords(normalizedMinus);
    } catch (err) {
      console.error('Ошибка загрузки запросов:', err);
      setError('Ошибка при загрузке запросов: ' + (err.response?.data?.error || err.message));
      setActiveKeywords([]);
      setMinusKeywords([]);
    } finally {
      setLoadingKeywords(false);
    }
  };

  const handleRemoveMinusPhrase = async (normQuery) => {
    if (!selectedCampaignId || !normQuery) return;
    const campaign = campaigns.find(c => c.advert_id === selectedCampaignId);
    setRemovingPhrase(normQuery);
    setError('');
    try {
      await apiClient.post('/set-excluded/remove', {
        client_id: Number(clientId),
        advert_id: Number(selectedCampaignId),
        nm_id: campaign?.nm_id ? Number(campaign.nm_id) : undefined,
        norm_query: normQuery,
      });
      setSuccessMessage(`Фраза «${normQuery}» возвращена в показы`);
      setTimeout(() => setSuccessMessage(''), 2500);
      await loadKeywords(selectedCampaignId, campaign?.nm_id);
      setCampaigns((prev) => prev.map((c) => (
        Number(c.advert_id) === Number(selectedCampaignId)
          ? { ...c, minus_count: Math.max(0, (c.minus_count || 0) - 1) }
          : c
      )));
    } catch (err) {
      setError('Не удалось вернуть фразу в показы: ' + (err.response?.data?.error || err.message));
    } finally {
      setRemovingPhrase(null);
    }
  };

  // Обработка клика на кампанию
  const handleCampaignClick = (campaign) => {
    setSelectedCampaignId(campaign.advert_id);
    setKeywordViewTab('active');
    setView('keywords');
    loadKeywords(campaign.advert_id, campaign.nm_id);
  };

  // Возврат к списку кампаний
  const handleBackToCampaigns = () => {
    setView('campaigns');
    setKeywordViewTab('active');
    setSelectedCampaignId(null);
    setActiveKeywords([]);
    setMinusKeywords([]);
    setError('');
    setSuccessMessage('');
  };

  // Выбор/снятие выбора кампании
  const handleToggleCampaign = (advertId, event) => {
    event.stopPropagation();
    const newSet = new Set(selectedCampaigns);
    if (newSet.has(advertId)) {
      newSet.delete(advertId);
    } else {
      newSet.add(advertId);
    }
    setSelectedCampaigns(newSet);
  };

  // Выбор всех кампаний
  const handleSelectAllCampaigns = (checked) => {
    if (checked) {
      setSelectedCampaigns(new Set(campaigns.map(c => c.advert_id)));
    } else {
      setSelectedCampaigns(new Set());
    }
  };

  // Открытие модального окна правил — подставляем сохранённые настройки или значения по умолчанию
  const handleOpenRulesModal = (advertId = null) => {
    if (advertId) {
      setRulesForCampaigns([advertId]);
    } else {
      setRulesForCampaigns(Array.from(selectedCampaigns));
    }
    const saved = loadRuleSettings();
    const settings = saved || DEFAULT_RULE_SETTINGS;
    setRuleShowsOp(settings.ruleShowsOp);
    setRuleShowsVal(settings.ruleShowsVal);
    setRuleCtrOp(settings.ruleCtrOp);
    setRuleCtrVal(settings.ruleCtrVal);
    setRuleOrdersOp(settings.ruleOrdersOp);
    setRuleOrdersVal(settings.ruleOrdersVal);
    setRuleCpoOp(settings.ruleCpoOp);
    setRuleCpoVal(settings.ruleCpoVal);
    setRuleExcludeInput(settings.ruleExcludeInput);
    setShowRulesModal(true);
  };

  // Удаление сохранённых настроек правил (сброс к значениям по умолчанию)
  const handleClearRuleSettings = () => {
    clearRuleSettings();
    setRuleShowsOp(DEFAULT_RULE_SETTINGS.ruleShowsOp);
    setRuleShowsVal(DEFAULT_RULE_SETTINGS.ruleShowsVal);
    setRuleCtrOp(DEFAULT_RULE_SETTINGS.ruleCtrOp);
    setRuleCtrVal(DEFAULT_RULE_SETTINGS.ruleCtrVal);
    setRuleOrdersOp(DEFAULT_RULE_SETTINGS.ruleOrdersOp);
    setRuleOrdersVal(DEFAULT_RULE_SETTINGS.ruleOrdersVal);
    setRuleCpoOp(DEFAULT_RULE_SETTINGS.ruleCpoOp);
    setRuleCpoVal(DEFAULT_RULE_SETTINGS.ruleCpoVal);
    setRuleExcludeInput(DEFAULT_RULE_SETTINGS.ruleExcludeInput);
    setError('');
  };

  // Применение правил
  const handleApplyRules = async () => {
    console.log('[handleApplyRules] Начало применения правил');
    console.log('[handleApplyRules] Параметры:', {
      rulesForCampaigns,
      ruleShowsOp,
      ruleShowsVal,
      ruleCtrOp,
      ruleCtrVal,
      ruleExcludeInput,
      clientId
    });

    if (rulesForCampaigns.length === 0) {
      console.error('[handleApplyRules] Не выбраны кампании');
      setError('Не выбраны кампании');
      return;
    }

    if (!ruleShowsVal || !ruleCtrVal) {
      console.error('[handleApplyRules] Не заполнены все поля фильтров');
      setError('Заполните все поля фильтров');
      return;
    }

    setApplyingRules(true);
    setError('');

    const excludeWords = ruleExcludeInput.split(',').map(w => w.trim()).filter(Boolean);
    console.log('[handleApplyRules] Исключающие слова:', excludeWords);

    try {
      let successCount = 0;
      let errorCount = 0;
      let skippedCount = 0;
      const succeededAdvertIds = [];

      for (const advertId of rulesForCampaigns) {
        console.log(`[handleApplyRules] Обработка кампании ${advertId}`);
        try {
          // Находим кампанию для получения nm_id
          const campaign = campaigns.find(c => c.advert_id === advertId);
          console.log(`[handleApplyRules] Найдена кампания:`, campaign);
          
          let nmId = campaign?.nm_id;
          
          // Если nm_id нет, пытаемся получить его из API
          if (!nmId) {
            console.log(`[handleApplyRules] nm_id не найден, пытаемся получить из API для кампании ${advertId}`);
            try {
              // Пробуем получить nm_id из /summary/campaigns
              const campaignRes = await apiClient.get('/summary/campaigns', {
                params: { client_id: Number(clientId) },
              });
              const foundCampaign = campaignRes.data?.find(c => c.advert_id === advertId);
              if (foundCampaign?.nm_id) {
                nmId = foundCampaign.nm_id;
                console.log(`[handleApplyRules] nm_id получен из API: ${nmId}`);
              } else {
                console.warn(`[handleApplyRules] nm_id не найден в API для кампании ${advertId}`);
              }
            } catch (err) {
              console.warn(`[handleApplyRules] Не удалось получить nm_id из API:`, err.message);
            }
          }
          
          // Если nm_id все еще нет, отправляем запрос без него - бэкенд попытается найти его сам
          if (!nmId) {
            console.log(`[handleApplyRules] nm_id не найден, отправляем запрос без nm_id - бэкенд попытается найти его`);
          }

          console.log(`[handleApplyRules] Параметры запроса:`, {
            client_id: Number(clientId),
            advert_id: Number(advertId),
            nm_id: nmId ? Number(nmId) : null,
            ctr_op: ruleCtrOp,
            ctr_val: Number(ruleCtrVal),
            shows_op: ruleShowsOp,
            shows_val: Number(ruleShowsVal),
          });

          // Получаем ключевые слова по условиям
          const requestParams = {
            client_id: Number(clientId),
            advert_id: Number(advertId),
            ctr_op: ruleCtrOp,
            ctr_val: Number(ruleCtrVal),
            shows_op: ruleShowsOp,
            shows_val: Number(ruleShowsVal),
          };
          if (ruleOrdersVal !== '' && ruleOrdersVal != null) {
            requestParams.orders_op = ruleOrdersOp;
            requestParams.orders_val = Number(ruleOrdersVal);
          }
          if (ruleCpoVal !== '' && ruleCpoVal != null) {
            requestParams.cpo_op = ruleCpoOp;
            requestParams.cpo_val = Number(ruleCpoVal);
          }
          if (usePeriod && periodFrom && periodTo) {
            requestParams.from = periodFrom;
            requestParams.to = periodTo;
          }
          if (nmId) {
            requestParams.nm_id = Number(nmId);
          }
          
          const keywordsRes = await apiClient.get('/summary', {
            params: requestParams,
          });

          console.log(`[handleApplyRules] Получено ключевых слов:`, keywordsRes.data?.length || 0);

          let keywordsToExclude = keywordsRes.data || [];

          // Фильтруем по исключающим словам
          if (excludeWords.length > 0) {
            keywordsToExclude = keywordsToExclude.filter((k) => {
              const query = (k.norm_query || k.keyword || '').toLowerCase();
              return !excludeWords.some((word) => query.includes(word.toLowerCase()));
            });
            console.log(`[handleApplyRules] После фильтрации по исключающим словам:`, keywordsToExclude.length);
          }

          if (keywordsToExclude.length > 0) {
            // Используем norm_query как основной идентификатор
            const excluded = keywordsToExclude
              .map(k => k.norm_query || k.keyword)
              .filter(Boolean)
              .filter((q, idx, arr) => arr.indexOf(q) === idx); // Убираем дубликаты

            console.log(`[handleApplyRules] Фраз для исключения:`, excluded.length);
            console.log(`[handleApplyRules] Первые 5 фраз:`, excluded.slice(0, 5));

            // Применяем минус-фразы через новый API
            try {
              console.log(`[handleApplyRules] Отправка запроса на /set-excluded:`, {
                client_id: Number(clientId),
                advert_id: Number(advertId),
                nm_id: nmId ? Number(nmId) : null,
                excluded_count: excluded.length
              });

              const requestBody = {
                client_id: Number(clientId),
                advert_id: Number(advertId),
                excluded: excluded,
              };
              
              // Добавляем nm_id только если он есть
              if (nmId) {
                requestBody.nm_id = Number(nmId);
              }

              const response = await apiClient.post('/set-excluded', requestBody);

              console.log(`[handleApplyRules] Ответ от API:`, response.data);
              if (response.data?.skipped) {
                skippedCount++;
                console.log(`[handleApplyRules] Кампания ${advertId} пропущена:`, response.data.message || 'кампания не найдена или отключена');
              } else {
                successCount++;
                succeededAdvertIds.push(Number(advertId));
                console.log(`[handleApplyRules] Правила применены для кампании ${advertId}, исключено ${excluded.length} фраз`);
              }
            } catch (apiErr) {
              const data = apiErr.response?.data;
              if (data?.skipped) {
                skippedCount++;
                console.log(`[handleApplyRules] Кампания ${advertId} пропущена:`, data.message || 'кампания не найдена или отключена');
              } else {
                const errorMsg = data?.error || apiErr.message || 'Неизвестная ошибка';
                console.error(`[handleApplyRules] Ошибка API для кампании ${advertId}:`, errorMsg);
                errorCount++;
              }
            }
          } else {
            console.log(`[handleApplyRules] Для кампании ${advertId} не найдено фраз для исключения по заданным условиям`);
            // Не считаем это ошибкой, просто нет данных для исключения
          }
        } catch (err) {
          console.error(`[handleApplyRules] Ошибка для кампании ${advertId}:`, {
            message: err.message,
            response: err.response?.data,
            status: err.response?.status,
            stack: err.stack
          });
          errorCount++;
        }
      }

      console.log(`[handleApplyRules] Итоги: successCount=${successCount}, skippedCount=${skippedCount}, errorCount=${errorCount}`);

      if (successCount > 0) {
        saveRuleSettings({
          ruleShowsOp,
          ruleShowsVal,
          ruleCtrOp,
          ruleCtrVal,
          ruleOrdersOp,
          ruleOrdersVal,
          ruleCpoOp,
          ruleCpoVal,
          ruleExcludeInput,
        });
        // Сохраняем правило на сервере только для кампаний, где минус-фразы реально применены
        try {
          for (const advertId of succeededAdvertIds) {
            await apiClient.post('/set-excluded/save-rule', {
              client_id: Number(clientId),
              advert_id: Number(advertId),
              ctr_op: ruleCtrOp,
              ctr_val: Number(ruleCtrVal),
              shows_op: ruleShowsOp,
              shows_val: Number(ruleShowsVal),
              orders_op: ruleOrdersOp || undefined,
              orders_val: ruleOrdersVal !== '' && ruleOrdersVal != null ? Number(ruleOrdersVal) : undefined,
              cpo_op: ruleCpoOp || undefined,
              cpo_val: ruleCpoVal !== '' && ruleCpoVal != null ? Number(ruleCpoVal) : undefined,
              exclude_words: ruleExcludeInput || '',
            });
          }
        } catch (saveErr) {
          console.warn('[handleApplyRules] Не удалось сохранить правило для крона:', saveErr.response?.data || saveErr.message);
        }
        setCampaigns((prev) => prev.map((c) => (
          succeededAdvertIds.includes(Number(c.advert_id))
            ? { ...c, has_autominus_rule: true }
            : c
        )));
        const parts = [`Правила применены для ${successCount} кампаний`];
        if (skippedCount > 0) parts.push(`пропущено: ${skippedCount} (отключены или не найдены)`);
        if (errorCount > 0) parts.push(`ошибок: ${errorCount}`);
        setSuccessMessage(parts.join(', '));
        setTimeout(() => {
          setSuccessMessage('');
          setShowRulesModal(false);
          loadCampaigns();
        }, 2000);
      } else if (errorCount > 0) {
        setError(`Не удалось применить правила. Ошибок: ${errorCount}${skippedCount > 0 ? `, пропущено кампаний: ${skippedCount}` : ''}. Проверьте консоль браузера для деталей.`);
      } else if (skippedCount > 0 && rulesForCampaigns.length === skippedCount) {
        setError(`Все выбранные кампании пропущены (не найдены или отключены). Проверьте, что кампании активны в рекламном кабинете WB.`);
      } else {
        setError('Не найдено фраз для исключения по заданным условиям. Попробуйте изменить параметры фильтров.');
      }
    } catch (err) {
      console.error('Ошибка применения правил:', err);
      setError('Ошибка при применении правил: ' + (err.response?.data?.error || err.message));
    } finally {
      setApplyingRules(false);
    }
  };

  // Получение цвета для ячейки CTR
  const getCtrColor = (ctr) => {
    if (ctr === 0 || ctr === null) {
      return 'text-gray-500';
    } else if (ctr < 1) {
      return 'text-red-700 font-bold';
    } else if (ctr < 3) {
      return 'text-yellow-600 font-bold';
    } else {
      return 'text-green-700 font-bold';
    }
  };

  // Получение цвета для ячейки CPC
  const getCpcColor = (cpc, ctr) => {
    if (cpc === 0 || cpc === null || ctr === 0) {
      return 'text-gray-500';
    } else if (ctr < 1) {
      return 'text-red-700 font-bold';
    } else if (ctr < 3) {
      return 'text-yellow-600 font-bold';
    } else {
      return 'text-green-700 font-bold';
    }
  };

  const selectedCampaign = campaigns.find(c => c.advert_id === selectedCampaignId);

  const renderKeywordTable = ({
    rows,
    sortCol,
    sortDir,
    onSort,
    showActions = false,
    emptyMessage = 'Нет запросов для отображения',
  }) => {
    const SortBtn = ({ col, label }) => (
      <button type="button" onClick={() => onSort(col)} className="flex items-center gap-0.5 hover:text-gray-700">
        {label}
        {sortCol === col && (sortDir === 'desc' ? <HiArrowDown className="w-3.5 h-3.5" /> : <HiArrowUp className="w-3.5 h-3.5" />)}
      </button>
    );

    const colgroup = (
      <colgroup>
        <col style={{ width: showActions ? '20%' : '22%' }} />
        <col style={{ width: '7%' }} />
        <col style={{ width: '7%' }} />
        <col style={{ width: '6%' }} />
        <col style={{ width: '7%' }} />
        <col style={{ width: '7%' }} />
        <col style={{ width: '8%' }} />
        <col style={{ width: '7%' }} />
        <col style={{ width: '7%' }} />
        <col style={{ width: '7%' }} />
        <col style={{ width: '8%' }} />
        {showActions && <col style={{ width: '11%' }} />}
      </colgroup>
    );

    const thClass = 'sticky top-0 z-20 bg-white py-2.5 text-left border-b border-slate-200 shadow-[0_1px_0_0_#e2e8f0]';

    return (
      <div className="flex-1 min-h-0 overflow-auto overscroll-contain">
        <table className="w-full min-w-[1024px] text-left text-sm table-fixed border-separate border-spacing-0">
          {colgroup}
          <thead className="text-xs uppercase text-gray-500">
            <tr>
              <th className={`${thClass} pl-4`}><SortBtn col="norm_query" label="Поисковый запрос" /></th>
              <th className={thClass}><SortBtn col="shows" label="Показы" /></th>
              <th className={thClass}><SortBtn col="clicks" label="Клики" /></th>
              <th className={thClass}><SortBtn col="ctr" label="CTR" /></th>
              <th className={thClass}><SortBtn col="avg_pos" label="Позиция" /></th>
              <th className={thClass}><SortBtn col="cpc" label="CPC" /></th>
              <th className={thClass}><SortBtn col="sum" label="Расход" /></th>
              <th className={thClass}><SortBtn col="orders" label="Заказы" /></th>
              <th className={thClass}><SortBtn col="cpo" label="CPO" /></th>
              <th className={thClass}><SortBtn col="bid" label="Ставка" /></th>
              <th className={thClass}><SortBtn col="last_updated" label="Обновлено" /></th>
              {showActions && <th className={`${thClass} pr-4`}>Действия</th>}
            </tr>
          </thead>
          <tbody className="bg-white">
            {!rows.length ? (
              <tr>
                <td colSpan={showActions ? 12 : 11} className="py-8 text-center text-gray-500 text-sm">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((keyword, index) => {
                const normQuery = keyword.norm_query || keyword.keyword || '';
                const uniqueKey = normQuery || `row-${index}`;
                const lastUpdated = keyword.last_updated
                  ? new Date(keyword.last_updated).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
                  : '—';
                return (
                  <tr key={uniqueKey} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 pl-4 bg-inherit">
                      <div className="font-medium truncate" title={normQuery}>
                        {normQuery || '-'}
                      </div>
                    </td>
                    <td className="py-3 bg-inherit">{(keyword.shows || 0).toLocaleString('ru-RU')}</td>
                    <td className="py-3 bg-inherit">{(keyword.clicks || 0).toLocaleString('ru-RU')}</td>
                    <td className={`py-3 bg-inherit ${getCtrColor(keyword.ctr || 0)}`}>
                      {((keyword.ctr || 0) !== null ? `${(keyword.ctr || 0).toFixed(2)}%` : '—')}
                    </td>
                    <td className="py-3 bg-inherit">
                      {(keyword.avg_pos ?? keyword.position) != null && (keyword.avg_pos ?? keyword.position) !== ''
                        ? Number(keyword.avg_pos ?? keyword.position).toFixed(2)
                        : '—'}
                    </td>
                    <td className={`py-3 bg-inherit ${getCpcColor(keyword.cpc || 0, keyword.ctr || 0)}`}>
                      {((keyword.cpc || 0) > 0 ? `${(keyword.cpc || 0).toFixed(2)} ₽` : '—')}
                    </td>
                    <td className="py-3 bg-inherit">
                      {((keyword.sum || 0) > 0
                        ? `${(keyword.sum || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽`
                        : '—')}
                    </td>
                    <td className="py-3 bg-inherit">{(keyword.orders || 0).toLocaleString('ru-RU')}</td>
                    <td className="py-3 bg-inherit">
                      {(() => {
                        const cpo = keyword.cpo ?? (keyword.orders > 0 && (keyword.sum || 0) > 0 ? (keyword.sum / keyword.orders) : null);
                        return cpo != null ? `${Number(cpo).toFixed(2)} ₽` : '—';
                      })()}
                    </td>
                    <td className="py-3 bg-inherit">
                      {keyword.bid && keyword.bid > 0 ? `${Number(keyword.bid).toFixed(2)} ₽` : '—'}
                    </td>
                    <td className="py-3 text-gray-500 bg-inherit">{lastUpdated}</td>
                    {showActions && (
                      <td className="py-3 pr-4 bg-inherit">
                        <button
                          type="button"
                          onClick={() => handleRemoveMinusPhrase(normQuery)}
                          disabled={removingPhrase === normQuery}
                          className="px-2.5 py-1 text-xs rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 whitespace-nowrap"
                        >
                          {removingPhrase === normQuery ? '…' : 'Вернуть в показы'}
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      {view === 'campaigns' && (
    <div className="flex flex-col min-h-[calc(100vh-5rem)]">
      <div className="glass-card p-6 flex flex-col min-h-0 flex-1 overflow-hidden" style={{ maxHeight: 'calc(100vh - 5rem)' }}>
        {/* Заголовок и период */}
        <div className="flex flex-wrap justify-between items-center gap-4 mb-4 shrink-0">
          <h2 className="text-lg font-semibold text-gray-700">
            Автоминусация ключевых фраз
          </h2>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-gray-500">Период:</span>
            <label className="flex items-center gap-1.5 text-sm">
              <span className="text-gray-500">От</span>
              <input
                type="date"
                value={periodFrom || ''}
                onChange={(e) => setPeriodFrom(e.target.value)}
                disabled={!usePeriod}
                className="border border-gray-300 rounded px-2 py-1.5 text-sm disabled:bg-gray-100"
              />
            </label>
            <label className="flex items-center gap-1.5 text-sm">
              <span className="text-gray-500">До</span>
              <input
                type="date"
                value={periodTo || ''}
                onChange={(e) => setPeriodTo(e.target.value)}
                disabled={!usePeriod}
                className="border border-gray-300 rounded px-2 py-1.5 text-sm disabled:bg-gray-100"
              />
            </label>
            <button
              type="button"
              onClick={() => {
                setUsePeriod(!usePeriod);
                if (usePeriod) {
                  setPeriodFrom(defaultFrom());
                  setPeriodTo(defaultTo());
                }
              }}
              className={`text-sm px-3 py-1.5 rounded border transition-colors ${usePeriod ? 'border-gray-300 text-gray-600 hover:bg-gray-50' : 'border-indigo-500 bg-indigo-50 text-indigo-700'}`}
            >
              {usePeriod ? 'Вся история' : 'Выбрать период'}
            </button>
          </div>
          <div className="flex gap-3">
            {selectedCampaigns.size > 0 && (
              <button
                onClick={() => handleOpenRulesModal()}
                className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 transition-colors text-sm"
              >
                Применить правила к выбранным ({selectedCampaigns.size})
              </button>
            )}
          </div>
        </div>

        {/* Сообщения */}
        {successMessage && (
          <div className="mb-4 text-center text-green-600 shrink-0">{successMessage}</div>
        )}
        {error && (
          <div className="mb-4 text-center text-red-600 shrink-0">{error}</div>
        )}

        {/* Список кампаний */}
          <>
            {loadingCampaigns ? (
              <div className="text-center text-gray-600">Загружается список кампаний...</div>
            ) : campaigns.length === 0 ? (
              <div className="text-center text-gray-500">Нет кампаний для отображения</div>
            ) : (
              <div className="flex flex-col min-h-0 flex-1">
                {/* Чекбокс "Выбрать все" */}
                <div className="mb-4 shrink-0 pl-3">
                  <label className="flex items-center cursor-pointer text-sm gap-2">
                    <input
                      type="checkbox"
                      checked={campaigns.length > 0 && campaigns.every(c => selectedCampaigns.has(c.advert_id))}
                      onChange={(e) => handleSelectAllCampaigns(e.target.checked)}
                    />
                    Выбрать все кампании
                  </label>
                </div>

                {/* Таблица кампаний */}
                <div className="overflow-x-auto overflow-y-auto flex-1 min-h-0 rounded-xl border border-white/50 bg-white/85 backdrop-blur-sm shadow-sm pl-4">
                  <table className="w-full text-left text-sm">
                    <thead className="text-xs uppercase text-gray-500 border-b border-slate-200/80 sticky top-0 z-10 bg-white/95 backdrop-blur-sm">
                      <tr>
                        <th className="py-2 w-14 pl-4 bg-white/95"></th>
                        <th className="py-2 bg-white/95">
                          <button type="button" onClick={() => toggleCampaignSort('name')} className="flex items-center gap-0.5 hover:text-gray-700">
                            Кампания
                            {campaignSortCol === 'name' && (campaignSortDir === 'desc' ? <HiArrowDown className="w-3.5 h-3.5" /> : <HiArrowUp className="w-3.5 h-3.5" />)}
                          </button>
                        </th>
                        <th className="py-2 bg-white/95">
                          <button type="button" onClick={() => toggleCampaignSort('impressions')} className="flex items-center gap-0.5 hover:text-gray-700">
                            Показы
                            {campaignSortCol === 'impressions' && (campaignSortDir === 'desc' ? <HiArrowDown className="w-3.5 h-3.5" /> : <HiArrowUp className="w-3.5 h-3.5" />)}
                          </button>
                        </th>
                        <th className="py-2 bg-white/95">
                          <button type="button" onClick={() => toggleCampaignSort('clicks')} className="flex items-center gap-0.5 hover:text-gray-700">
                            Клики
                            {campaignSortCol === 'clicks' && (campaignSortDir === 'desc' ? <HiArrowDown className="w-3.5 h-3.5" /> : <HiArrowUp className="w-3.5 h-3.5" />)}
                          </button>
                        </th>
                        <th className="py-2 bg-white/95">
                          <button type="button" onClick={() => toggleCampaignSort('spend')} className="flex items-center gap-0.5 hover:text-gray-700">
                            Расход
                            {campaignSortCol === 'spend' && (campaignSortDir === 'desc' ? <HiArrowDown className="w-3.5 h-3.5" /> : <HiArrowUp className="w-3.5 h-3.5" />)}
                          </button>
                        </th>
                        <th className="py-2 bg-white/95">
                          <button type="button" onClick={() => toggleCampaignSort('orders')} className="flex items-center gap-0.5 hover:text-gray-700">
                            Заказы
                            {campaignSortCol === 'orders' && (campaignSortDir === 'desc' ? <HiArrowDown className="w-3.5 h-3.5" /> : <HiArrowUp className="w-3.5 h-3.5" />)}
                          </button>
                        </th>
                        <th className="py-2 bg-white/95">
                          <button type="button" onClick={() => toggleCampaignSort('ctr')} className="flex items-center gap-0.5 hover:text-gray-700">
                            CTR
                            {campaignSortCol === 'ctr' && (campaignSortDir === 'desc' ? <HiArrowDown className="w-3.5 h-3.5" /> : <HiArrowUp className="w-3.5 h-3.5" />)}
                          </button>
                        </th>
                        <th className="py-2 bg-white/95">
                          <button type="button" onClick={() => toggleCampaignSort('cpc')} className="flex items-center gap-0.5 hover:text-gray-700">
                            CPC
                            {campaignSortCol === 'cpc' && (campaignSortDir === 'desc' ? <HiArrowDown className="w-3.5 h-3.5" /> : <HiArrowUp className="w-3.5 h-3.5" />)}
                          </button>
                        </th>
                        <th className="py-2 bg-white/95">
                          <button type="button" onClick={() => toggleCampaignSort('cpo')} className="flex items-center gap-0.5 hover:text-gray-700">
                            CPO
                            {campaignSortCol === 'cpo' && (campaignSortDir === 'desc' ? <HiArrowDown className="w-3.5 h-3.5" /> : <HiArrowUp className="w-3.5 h-3.5" />)}
                          </button>
                        </th>
                        <th className="py-2 bg-white/95">
                          <button type="button" onClick={() => toggleCampaignSort('avg_pos')} className="flex items-center gap-0.5 hover:text-gray-700">
                            Позиция
                            {campaignSortCol === 'avg_pos' && (campaignSortDir === 'desc' ? <HiArrowDown className="w-3.5 h-3.5" /> : <HiArrowUp className="w-3.5 h-3.5" />)}
                          </button>
                        </th>
                        <th className="py-2 bg-white/95">
                          <button type="button" onClick={() => toggleCampaignSort('minus_count')} className="flex items-center gap-0.5 hover:text-gray-700">
                            Минус-фраз
                            {campaignSortCol === 'minus_count' && (campaignSortDir === 'desc' ? <HiArrowDown className="w-3.5 h-3.5" /> : <HiArrowUp className="w-3.5 h-3.5" />)}
                          </button>
                        </th>
                        <th className="py-2 bg-white/95">Действия</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white/70">
                      {sortedCampaigns.map((campaign) => (
                        <tr
                          key={campaign.advert_id}
                          className="border-b border-slate-100 cursor-pointer hover:bg-white/90"
                          onClick={() => handleCampaignClick(campaign)}
                        >
                          <td className="py-3 pl-4" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedCampaigns.has(campaign.advert_id)}
                              onChange={(e) => handleToggleCampaign(campaign.advert_id, e)}
                            />
                          </td>
                          <td className="py-3">
                            <div className="font-medium">{campaign.name || `Кампания ${campaign.advert_id}`}</div>
                            {campaign.nm_id && (
                              <div className="text-xs text-gray-400">№ {campaign.advert_id} · арт. {campaign.nm_id}</div>
                            )}
                            {!campaign.nm_id && (
                              <div className="text-xs text-gray-400">ID: {campaign.advert_id}</div>
                            )}
                          </td>
                          <td className="py-3">
                            {(campaign.metrics?.impressions || 0).toLocaleString('ru-RU')}
                          </td>
                          <td className="py-3">
                            {(campaign.metrics?.clicks || 0).toLocaleString('ru-RU')}
                          </td>
                          <td className="py-3">
                            {(campaign.metrics?.spend || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽
                          </td>
                          <td className="py-3">
                            {(campaign.metrics?.orders || 0).toLocaleString('ru-RU')}
                          </td>
                          <td className={`py-3 ${getCtrColor(campaign.metrics?.ctr || 0)}`}>
                            {((campaign.metrics?.ctr || 0) !== null ? `${(campaign.metrics?.ctr || 0).toFixed(2)}%` : '—')}
                          </td>
                          <td className={`py-3 ${getCpcColor(campaign.metrics?.cpc || 0, campaign.metrics?.ctr || 0)}`}>
                            {((campaign.metrics?.cpc || 0) !== null ? `${(campaign.metrics?.cpc || 0).toFixed(2)} ₽` : '—')}
                          </td>
                          <td className="py-3">
                            {campaign.metrics?.cpo != null ? `${Number(campaign.metrics.cpo).toFixed(2)} ₽` : '—'}
                          </td>
                          <td className="py-3">
                            {campaign.metrics?.avg_pos != null && campaign.metrics?.avg_pos !== ''
                              ? Number(campaign.metrics.avg_pos).toFixed(2)
                              : '—'}
                          </td>
                          <td className="py-3">
                            <span className={campaign.minus_count > 0 ? 'text-red-700 font-medium' : 'text-gray-500'}>
                              {(campaign.minus_count || 0).toLocaleString('ru-RU')}
                            </span>
                          </td>
                          <td className="py-3" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              onClick={() => handleOpenRulesModal(campaign.advert_id)}
                              className={rulesButtonClass(campaignHasActiveRule(campaign.advert_id))}
                              title={campaignHasActiveRule(campaign.advert_id) ? 'Правило автоминусации включено' : 'Правило не настроено'}
                            >
                              Правила
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
      </div>
    </div>
      )}

      {view === 'keywords' && (
        <div className="flex flex-col h-[calc(100vh-11rem)] min-h-[320px] max-h-[calc(100vh-11rem)] overflow-hidden">
          <div
            className="glass-card flex flex-col min-h-0 flex-1 overflow-hidden p-0 h-full"
          >
          <div className="shrink-0 bg-white/90 border-b border-slate-200/80 rounded-t-xl">
            <div className="px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2">
              <button
                type="button"
                onClick={handleBackToCampaigns}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm shrink-0"
              >
                ← Назад
              </button>
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-semibold text-gray-800 truncate">
                  {selectedCampaign?.name || `Кампания ${selectedCampaignId}`}
                </h3>
                {selectedCampaign?.nm_id ? (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Кампания № {selectedCampaignId} · арт. {selectedCampaign.nm_id}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-0.5">ID: {selectedCampaignId}</p>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <span className="text-xs text-gray-500">Период:</span>
                <input
                  type="date"
                  value={periodFrom || ''}
                  onChange={(e) => setPeriodFrom(e.target.value)}
                  disabled={!usePeriod}
                  className="border border-gray-300 rounded px-2 py-1 text-xs disabled:bg-gray-100"
                />
                <span className="text-xs text-gray-400">—</span>
                <input
                  type="date"
                  value={periodTo || ''}
                  onChange={(e) => setPeriodTo(e.target.value)}
                  disabled={!usePeriod}
                  className="border border-gray-300 rounded px-2 py-1 text-xs disabled:bg-gray-100"
                />
                <button
                  type="button"
                  onClick={() => {
                    setUsePeriod(!usePeriod);
                    if (usePeriod) {
                      setPeriodFrom(defaultFrom());
                      setPeriodTo(defaultTo());
                    }
                  }}
                  className={`text-xs px-2.5 py-1 rounded border transition-colors ${usePeriod ? 'border-gray-300 text-gray-600 hover:bg-gray-50' : 'border-indigo-500 bg-indigo-50 text-indigo-700'}`}
                >
                  {usePeriod ? 'Вся история' : 'Период'}
                </button>
              </div>
            </div>

            <div className="px-4 pb-3 flex flex-wrap items-center gap-3">
              <div className="inline-flex rounded-lg border border-gray-200 bg-gray-100 p-0.5">
                <button
                  type="button"
                  onClick={() => setKeywordViewTab('active')}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    keywordViewTab === 'active'
                      ? 'bg-white text-gray-800 shadow-sm font-medium'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Активные фразы ({activeKeywords.length})
                </button>
                <button
                  type="button"
                  onClick={() => setKeywordViewTab('minus')}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    keywordViewTab === 'minus'
                      ? 'bg-white text-gray-800 shadow-sm font-medium'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Минус-фразы ({minusKeywords.length})
                </button>
              </div>
            </div>

            {(successMessage || error) && (
              <div className="px-4 pb-3">
                {successMessage && (
                  <div className="text-sm text-center text-green-600">{successMessage}</div>
                )}
                {error && (
                  <div className="text-sm text-center text-red-600">{error}</div>
                )}
              </div>
            )}
          </div>

          <div className="flex-1 min-h-0 p-4 pt-3 overflow-hidden flex flex-col">
            {loadingKeywords ? (
              <div className="flex-1 flex items-center justify-center text-gray-600">Загружаются запросы...</div>
            ) : (
              <div className={`flex-1 min-h-0 rounded-xl border bg-white shadow-sm overflow-hidden flex flex-col ${keywordViewTab === 'minus' ? 'border-red-100' : 'border-slate-200'}`}>
                {renderKeywordTable({
                  rows: keywordViewTab === 'minus' ? sortedMinusKeywords : sortedActiveKeywords,
                  sortCol: keywordViewTab === 'minus' ? minusSortCol : keywordSortCol,
                  sortDir: keywordViewTab === 'minus' ? minusSortDir : keywordSortDir,
                  onSort: keywordViewTab === 'minus' ? toggleMinusSort : toggleKeywordSort,
                  showActions: keywordViewTab === 'minus',
                  emptyMessage: keywordViewTab === 'minus'
                    ? 'Минус-фраз пока нет'
                    : 'Нет активных запросов за выбранный период',
                })}
              </div>
            )}
          </div>
          </div>
        </div>
      )}

      {/* Модальное окно правил */}
      {showRulesModal && (
        <ModalOverlay onClick={() => { if (!applyingRules) { setShowRulesModal(false); setError(''); } }}>
          <div className="modal-panel rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Шапка */}
            <div className="modal-header flex items-center justify-between px-6 py-4 shrink-0 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-semibold text-gray-800">
                  Правила автоминусации
                </h3>
                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                  {rulesForCampaigns.length} {rulesForCampaigns.length === 1 ? 'кампания' : rulesForCampaigns.length < 5 ? 'кампании' : 'кампаний'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => { setShowRulesModal(false); setError(''); }}
                disabled={applyingRules}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                aria-label="Закрыть"
              >
                <HiX className="w-5 h-5" />
              </button>
            </div>

            {/* Контент */}
            <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">
              <p className="text-sm text-gray-500 mb-4">
                В минус-фразы попадут запросы, подходящие под все заданные условия.
              </p>

              <section className="mb-5">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Условия отбора</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700 w-20 shrink-0">Показы</span>
                    <select
                      value={ruleShowsOp}
                      onChange={(e) => setRuleShowsOp(e.target.value)}
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm min-w-[11rem] bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value=">=">больше или равно</option>
                      <option value="<=">меньше или равно</option>
                      <option value="=">равно</option>
                      <option value=">">больше</option>
                      <option value="<">меньше</option>
                    </select>
                    <input
                      type="number"
                      value={ruleShowsVal}
                      onChange={(e) => setRuleShowsVal(e.target.value)}
                      placeholder="0"
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm flex-1 min-w-0 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700 w-20 shrink-0">CTR, %</span>
                    <select
                      value={ruleCtrOp}
                      onChange={(e) => setRuleCtrOp(e.target.value)}
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm min-w-[11rem] bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="<=">меньше или равно</option>
                      <option value=">=">больше или равно</option>
                      <option value="=">равно</option>
                      <option value=">">больше</option>
                      <option value="<">меньше</option>
                    </select>
                    <input
                      type="number"
                      step="0.01"
                      value={ruleCtrVal}
                      onChange={(e) => setRuleCtrVal(e.target.value)}
                      placeholder="0"
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm flex-1 min-w-0 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 w-20 shrink-0">Заказы</span>
                    <select
                      value={ruleOrdersOp}
                      onChange={(e) => setRuleOrdersOp(e.target.value)}
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm min-w-[11rem] bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="<=">меньше или равно</option>
                      <option value=">=">больше или равно</option>
                      <option value="=">равно</option>
                      <option value=">">больше</option>
                      <option value="<">меньше</option>
                    </select>
                    <input
                      type="number"
                      value={ruleOrdersVal}
                      onChange={(e) => setRuleOrdersVal(e.target.value)}
                      placeholder="—"
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm flex-1 min-w-0 placeholder:text-gray-300 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 w-20 shrink-0">CPO, ₽</span>
                    <select
                      value={ruleCpoOp}
                      onChange={(e) => setRuleCpoOp(e.target.value)}
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm min-w-[11rem] bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value=">=">больше или равно</option>
                      <option value="<=">меньше или равно</option>
                      <option value="=">равно</option>
                      <option value=">">больше</option>
                      <option value="<">меньше</option>
                    </select>
                    <input
                      type="number"
                      step="0.01"
                      value={ruleCpoVal}
                      onChange={(e) => setRuleCpoVal(e.target.value)}
                      placeholder="—"
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm flex-1 min-w-0 placeholder:text-gray-300 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2">Заказы и CPO необязательны: пустое поле — условие не учитывается.</p>
              </section>

              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Не включать фразы со словами</h4>
                <input
                  type="text"
                  value={ruleExcludeInput}
                  onChange={(e) => setRuleExcludeInput(e.target.value)}
                  placeholder="бренд, конкурент — через запятую"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm placeholder:text-gray-300 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </section>

              {error && (
                <div className="mt-4 px-3 py-2 rounded-lg bg-red-50 text-red-700 text-sm">
                  {error}
                </div>
              )}
            </div>

            {/* Подвал */}
            <div className="modal-footer flex justify-between items-center gap-3 px-6 py-4 shrink-0 rounded-b-2xl">
              <button
                type="button"
                onClick={handleClearRuleSettings}
                disabled={applyingRules}
                className="px-3 py-2 text-sm font-medium text-gray-500 hover:text-red-600 transition-colors disabled:opacity-50"
                title="Сбросить сохранённые настройки к значениям по умолчанию"
              >
                Удалить правила
              </button>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setShowRulesModal(false); setError(''); }}
                  disabled={applyingRules}
                  className="glass-btn-secondary px-4 py-2 text-sm font-medium disabled:opacity-50"
                >
                  Отмена
                </button>
                <button
                  type="button"
                  onClick={handleApplyRules}
                  disabled={applyingRules || !ruleShowsVal || !ruleCtrVal}
                  className={`px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
                    applyingRules || !ruleShowsVal || !ruleCtrVal
                      ? 'bg-gray-200/80 text-gray-400 cursor-not-allowed'
                      : 'glass-btn'
                  }`}
                >
                  {applyingRules ? 'Применяем…' : 'Применить'}
                </button>
              </div>
            </div>
          </div>
        </ModalOverlay>
      )}
    </>
  );
}
